// Lazy Iconify collections used by military-plan markers. Keeping collection
// JSON behind dynamic imports avoids adding 12k icons to initial map bundle.

export const TACTICAL_ICON_SOURCES = {
  'game-icons': { label: 'Game Icons', count: 4134, license: 'CC BY 3.0' },
  tabler: { label: 'Tabler', count: 6232, license: 'MIT' },
  lucide: { label: 'Lucide', count: 1843, license: 'ISC' },
}

const loaders = {
  'game-icons': () => import('@iconify-json/game-icons/icons.json'),
  tabler: () => import('@iconify-json/tabler/icons.json'),
  lucide: () => import('@iconify-json/lucide/icons.json'),
}
const collections = new Map()
const pending = new Map()
let militaryCatalog = null
let militaryCatalogPending = null

export const isTacticalIcon = (value) => {
  const source = String(value || '').split(':', 1)[0]
  return Object.hasOwn(TACTICAL_ICON_SOURCES, source)
}

export const loadTacticalCollection = async (source) => {
  if (collections.has(source)) return collections.get(source)
  if (!loaders[source]) return null
  if (!pending.has(source)) {
    pending.set(source, loaders[source]().then((module) => {
      const collection = module.default || module
      collections.set(source, collection)
      pending.delete(source)
      return collection
    }).catch((error) => {
      pending.delete(source)
      throw error
    }))
  }
  return pending.get(source)
}

export const tacticalIconRef = (source, name) => `${source}:${name}`

export const tacticalIconName = (ref) => {
  const name = String(ref || '').split(':').slice(1).join(':')
  return name.replace(/-/g, ' ')
}

export const loadMilitaryCatalog = async () => {
  if (militaryCatalog) return militaryCatalog
  if (!militaryCatalogPending) {
    militaryCatalogPending = import('milstandard-e').then((module) => {
      const standard = module.ms2525e || module.default?.ms2525e
      militaryCatalog = Object.entries(standard || {}).flatMap(([symbolSet, data]) => {
        if (symbolSet === 'common') return []
        return (data.mainIcon || []).flatMap((entry) => {
          const parts = [entry.Entity, entry['Entity Type'], entry['Entity Subtype']]
            .map((part) => String(part || '').trim()).filter((part, index, all) => part && part !== all[index - 1])
          if (!parts.length || parts.some((part) => part.includes('{Disused}'))) return []
          const sidc = `1303${symbolSet}0000${entry.Code}00000000000000`
          return [{ id: `milstd-e-${symbolSet}-${entry.Code}`, group: data.name, name: parts.join(' · '), sidc }]
        })
      })
      militaryCatalogPending = null
      return militaryCatalog
    }).catch((error) => {
      militaryCatalogPending = null
      throw error
    })
  }
  return militaryCatalogPending
}

export const searchMilitaryCatalog = (query = '') => {
  const words = query.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean)
  return (militaryCatalog || []).filter((symbol) => {
    const haystack = `${symbol.group} ${symbol.name} ${symbol.sidc}`.toLocaleLowerCase()
    return words.every((word) => haystack.includes(word))
  })
}

export const searchTacticalIcons = (source, query = '') => {
  const collection = collections.get(source)
  if (!collection) return []
  const words = query.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean)
  return Object.keys(collection.icons).filter((name) => words.every((word) => name.includes(word)))
}

export const tacticalIconSvg = (ref, size = 36, color = 'currentColor') => {
  const [source, ...nameParts] = String(ref || '').split(':')
  const name = nameParts.join(':')
  const collection = collections.get(source)
  const icon = collection?.icons?.[name]
  if (!icon) return '<span class="milplan-icon-loading">…</span>'
  const safeColor = /^#[0-9a-f]{3,8}$/i.test(color) || color === 'currentColor' ? color : 'currentColor'
  const width = icon.width || collection.width || 24
  const height = icon.height || collection.height || 24
  const rotate = Number(icon.rotate || 0) % 4
  const flipH = !!icon.hFlip
  const flipV = !!icon.vFlip
  let transform = ''
  if (rotate || flipH || flipV) {
    const transforms = []
    if (flipH) transforms.push(`translate(${width} 0) scale(-1 1)`)
    if (flipV) transforms.push(`translate(0 ${height}) scale(1 -1)`)
    if (rotate === 1) transforms.push(`translate(${height} 0) rotate(90)`)
    if (rotate === 2) transforms.push(`translate(${width} ${height}) rotate(180)`)
    if (rotate === 3) transforms.push(`translate(0 ${width}) rotate(270)`)
    transform = ` transform="${transforms.join(' ')}"`
  }
  const viewWidth = rotate % 2 ? height : width
  const viewHeight = rotate % 2 ? width : height
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${viewWidth} ${viewHeight}" role="img" aria-label="${name.replace(/&/g, '&amp;').replace(/"/g, '&quot;')}"><g color="${safeColor}"${transform}>${icon.body}</g></svg>`
}
